
import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useCounties } from "@/hooks/use-counties";
import { useCountyData } from "@/hooks/use-county-data";
import { useNursingHomes } from "@/hooks/use-nursing-homes";

export interface NursingHomeFilters {
  inspectionRating: string | null;
  fairDealOnly: boolean;
  priceRange: string | null;
  category: string | null;
  maxDistanceKm: number | null;
  userLocation: { lat: number; lng: number } | null;
}

const defaultFilters: NursingHomeFilters = {
  inspectionRating: null,
  fairDealOnly: false,
  priceRange: null,
  category: null,
  maxDistanceKm: null,
  userLocation: null,
};

function parsePriceFromRange(priceRange: string | null): number | null {
  if (!priceRange) return null;
  const match = priceRange.match(/€([\d,]+)/);
  if (match) return parseInt(match[1].replace(",", ""), 10);
  return null;
}

function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function useCountyPageData(countySlug: string | undefined) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<string>("name_asc");
  const [selectedCounty, setSelectedCounty] = useState<string | null>(null);
  const [filters, setFilters] = useState<NursingHomeFilters>(defaultFilters);
  
  // Reset selected county when navigating to a new county page
  useEffect(() => {
    setSelectedCounty(null);
  }, [location.pathname]);
  
  // Fetch counties for the dropdown
  const { data: counties, isLoading: countiesLoading } = useCounties();
  
  // Fetch specific county data
  const { data: countyData, isLoading: countyDataLoading } = useCountyData(countySlug);
  
  // Fetch nursing homes based on current filters
  const { data: nursingHomes, isLoading, error } = useNursingHomes(
    selectedCounty || countySlug, 
    searchQuery, 
    sortBy
  );

  // Apply client-side filters
  const filteredNursingHomes = useMemo(() => {
    if (!nursingHomes) return [];
    
    return nursingHomes.filter((home: any) => {
      // Inspection rating filter
      if (filters.inspectionRating && home.inspection_rating !== filters.inspectionRating) {
        return false;
      }
      
      // Fair Deal filter
      if (filters.fairDealOnly && home.fair_deal_approved !== true) {
        return false;
      }
      
      // Category filter
      if (filters.category && home.category !== filters.category) {
        return false;
      }

      // Price range filter
      if (filters.priceRange) {
        const minPrice = parsePriceFromRange(home.price_range);
        if (minPrice === null) return false;
        
        switch (filters.priceRange) {
          case "under_1000":
            if (minPrice >= 1000) return false;
            break;
          case "1000_1300":
            if (minPrice < 1000 || minPrice > 1300) return false;
            break;
          case "1300_1600":
            if (minPrice < 1300 || minPrice > 1600) return false;
            break;
          case "over_1600":
            if (minPrice < 1600) return false;
            break;
        }
      }

      // Distance filter
      if (filters.userLocation && filters.maxDistanceKm) {
        if (!home.latitude || !home.longitude) return false;
        const dist = haversineKm(
          filters.userLocation.lat,
          filters.userLocation.lng,
          home.latitude,
          home.longitude
        );
        if (dist > filters.maxDistanceKm) return false;
      }
      
      return true;
    });
  }, [nursingHomes, filters]);

  // Update the URL if the selected county changes through the dropdown
  useEffect(() => {
    if (selectedCounty && selectedCounty !== countySlug) {
      navigate(`/nursing-homes/${selectedCounty}`);
    }
  }, [selectedCounty, navigate, countySlug]);

  // Format county name from slug
  const formatCountyName = (slug?: string) => {
    if (!slug) return '';
    return slug
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  
  const countyName = countyData?.name || formatCountyName(countySlug);
  
  // Format counties for dropdown
  const countyOptions = counties?.map(county => ({
    county: county.name,
    county_slug: county.slug
  })) || [];

  // Find nearby counties for cross-linking
  const nearbyCounties = counties?.filter(county => 
    county.slug !== countySlug
  ).slice(0, 4) || [];

  const activeFilterCount = [
    filters.inspectionRating,
    filters.fairDealOnly,
    filters.priceRange,
    filters.category,
    filters.maxDistanceKm,
  ].filter(Boolean).length;

  return {
    countyName,
    nursingHomes: filteredNursingHomes,
    counties,
    countyData,
    nearbyCounties,
    searchQuery,
    setSearchQuery,
    sortBy,
    setSortBy,
    selectedCounty,
    setSelectedCounty,
    isLoading,
    error,
    countiesLoading,
    countyOptions,
    filters,
    setFilters,
    activeFilterCount,
  };
}
